import { useEffect, useRef, useState } from 'react';

import { MenuIcon } from '@/components/MenuIcon';
import { Switch } from '@/components/Switch';
import { Modal } from '@/components/Modal';
import {
  FONT_SCALES,
  FONT_SCALE_LABELS,
  readFontScale,
  saveFontScale,
  type FontScale,
} from '@/lib/ui/fontScale';
import { moveItem, useListDrag } from '@/pages/meetings/components/useListDrag';
import { useGreetingPreference } from '@/pages/home/useHomeGreeting';
import type { WidgetSlot } from '@/pages/home/useHomeWidgets';
import { widgetByKey, type WidgetSize } from '@/pages/home/widgets/registry';

/**
 * 위젯 관리.
 *
 * 왼쪽은 지금 홈에 있는 것, 오른쪽은 더 넣을 수 있는 것이다. 편집은 <b>이 모달 안에서만</b> 일어나고
 * 저장을 눌러야 반영된다 — 홈 화면에서 카드를 직접 끌어 옮기는 방식이 더 직관적이지만,
 * 좁은 화면과 터치에서 드래그는 스크롤과 싸우게 되고 현장에서 태블릿으로 보는 사람이 적지 않다.
 *
 * 고정 위젯은 끄지도, 선택 위젯 아래로 내리지도 못한다. 순서만 고정 위젯끼리 바꾼다.
 * **지금 정적 고정(`registry.ts` 의 `fixed`)인 위젯은 하나도 없다**(사용자 지시 2026-09-23 —
 * 할일·공지의 고정을 풀었다). 규칙과 그림은 남겨 둔다: 다시 고정할 위젯이 생겼을 때 여기가 그 자리다.
 *
 * 고정에는 두 겹이 있다(DOC-61 §11). `registry.ts` 의 `fixed` 는 코드에 박힌 것이고,
 * `forced` 는 **지금 조건이 만든 것**이다(착공 준비 알림 — 계약 날인 ~ 착공 사이). 조건 고정은
 * 「저절로 켜진다」 이지 「못 끈다」 가 아니라, 「자동」 딱지를 달고 닫는 버튼은 남긴다.
 *
 * ## 제목 줄의 실험실 스위치는 위젯이 아니다
 *
 * 홈 제목 줄의 인사말(DOC-66)을 켜고 끄는 스위치가 **모달 제목 줄**에 있다. 본문에 두면
 * 위젯 두 목록 위를 가로로 가로막아 주인공보다 먼저 읽힌다. **기본값은 켜짐**이다 —
 * 꺼 둔 채 먼저 써 보고 쓸 만해서 뒤집었다(사용자 지시 2026-08-27). **위젯 목록에 섞지 않았다** —
 * `mergeSlots` 는 카탈로그(`registry.ts`)에 없는 키를 버리므로, 위젯인 척 끼워 넣으면 사용자는
 * 껐다고 믿는데 다음 조회에서 그 줄이 사라지고 인사말은 다시 켜진다. 저장도 따로 간다:
 * 위젯 배치는 [저장] 을 눌러야 반영되지만 **이 스위치는 누르는 즉시 저장된다** — 켜고 끄는
 * 결과가 곧바로 보이는 편이 낫고, 취소로 되돌릴 것이 하나도 없다.
 *
 * 잠깐 **현장 정보 신규 디자인** 스위치가 같은 자리에 섰다가 빠졌다(2026-09-07 → 09-08). 시안이
 * 채택되어 현장 정보의 기본 카드가 되었고, 고를 것이 없어진 스위치는 지웠다 —
 * 한쪽만 남은 선택지를 스위치로 두면 끄는 사람이 옛 화면을 찾다가 못 찾는다.
 */
export function WidgetManagerModal({
  slots,
  forced,
  onSave,
  onReset,
  onCustomize,
  onClose,
}: {
  slots: WidgetSlot[];
  /** 지금 조건이 강제하는 위젯 키. 정적 고정(`fixed`)과 같이 다룬다. */
  forced: Set<string>;
  onSave: (next: WidgetSlot[]) => void;
  onReset: () => void;
  /** 「배치 커스텀」 — 이 모달을 닫고 홈을 편집 상태로 넘긴다({@link WidgetLayoutEditor}). */
  onCustomize: (next: WidgetSlot[]) => void;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState<WidgetSlot[]>(slots);
  const [keyword, setKeyword] = useState('');
  const greeting = useGreetingPreference();

  /**
   * 사용자가 이 모달에서 <b>무엇이든 손댔는가</b>.
   *
   * 사이드바에서 `/home?widgets=1` 로 들어오면 <b>서버 응답보다 모달이 먼저</b> 뜬다. 그때
   * `slots` 는 저장된 배치가 아니라 기본값이고, 잠시 뒤 진짜 배치가 도착한다. 손대기 전이라면
   * 도착한 것을 그대로 받아야 한다 — 안 받으면 사용자가 맞춰 둔 좌표·배율을 모르는 채로
   * 「배치 커스텀」 에 넘겨주고, <b>저장하는 순간 배치가 통째로 날아간다</b>(2026-09-16).
   *
   * 손댄 뒤에는 편집 중인 것을 지키고 새로 생긴 위젯만 뒤에 붙인다 — 고르던 것이 응답 하나에
   * 되돌아가면 그게 더 나쁘다.
   */
  const touched = useRef(false);

  useEffect(() => {
    if (!touched.current) {
      setDraft(slots);
      return;
    }

    setDraft((prev) => {
      const known = new Set(prev.map((slot) => slot.widgetKey));
      const added = slots.filter((slot) => !known.has(slot.widgetKey));
      return added.length === 0 ? prev : [...prev, ...added];
    });
  }, [slots]);

  /** 초안을 고친다. 고쳤다는 사실을 함께 남겨 늦게 온 응답이 덮지 못하게 한다. */
  const edit = (next: (prev: WidgetSlot[]) => WidgetSlot[]) => {
    touched.current = true;
    setDraft(next);
  };

  const visible = draft.filter((slot) => slot.visible);
  const hidden = draft.filter((slot) => !slot.visible);

  const matches = (slot: WidgetSlot) => {
    const definition = widgetByKey(slot.widgetKey);
    if (!definition) return false;
    if (!keyword.trim()) return true;

    const haystack = `${definition.title} ${definition.description}`;
    return haystack.includes(keyword.trim());
  };

  /**
   * 켜고 끈다. <b>켤 때는 크기를 기본값으로 되돌린다</b> — 꺼져 있던 위젯에 저장된 크기는
   * 사용자가 고른 값이 아니라 그 위젯이 목록에 처음 붙던 날의 기본값이다(`mergeSlots` 는 숨은
   * 위젯의 크기까지 함께 저장한다). 그대로 두면 기본 크기를 바꿔도 이미 쓰던 사람에게는
   * 옛 크기로 추가된다.
   */
  const setVisibility = (widgetKey: string, next: boolean) => {
    const defaultSize = widgetByKey(widgetKey)?.defaultSize;
    edit((prev) =>
      prev.map((slot) =>
        slot.widgetKey === widgetKey
          ? { ...slot, visible: next, size: next && defaultSize ? defaultSize : slot.size }
          : slot,
      ),
    );
  };

  const setSize = (widgetKey: string, size: WidgetSize) => {
    edit((prev) => prev.map((slot) => (slot.widgetKey === widgetKey ? { ...slot, size } : slot)));
  };

  const { dragHandlers, dragStyle } = useListDrag((draggedKey, targetKey) => {
    // 고정 위젯은 고정 무리 안에서만 움직인다. 무리를 건너뛰는 드롭은 아무 일도 하지 않는다.
    if (pinned(draggedKey) !== pinned(targetKey)) return;

    // moveItem 은 `id` 로 항목을 찾는다. 위젯의 식별자는 widgetKey 이므로 잠깐 이름을 맞춰 준다.
    const withId = draft.map((slot) => ({ ...slot, id: slot.widgetKey }));
    const moved = moveItem(withId, draggedKey, targetKey);
    if (!moved) return;

    // 붙여 둔 `id` 만 떼고 <b>나머지는 그대로</b> 남긴다. 세 칸만 골라 새로 만들면 좌표·배율·핀이
    // 조용히 지워진다 — 순서를 한 번 바꿨다고 맞춰 둔 배치가 날아가면 안 된다.
    edit(() =>
      moved.map((item) => {
        const slot = { ...item } as Partial<typeof item>;
        delete slot.id;
        return slot as WidgetSlot;
      }),
    );
  });

  return (
    <Modal
      title="위젯 관리"
      maxWidth="880px"
      onClose={onClose}
      /*
        버튼은 <b>제목 줄</b>에 둔다 — 이 앱의 모든 모달이 그렇다(사용자 지시). 목록이 둘이라
        창이 길고, 아래에 두면 위젯을 하나 고를 때마다 저장하러 끝까지 내려가야 한다.

        <p>좁은 화면에서 이 줄이 두 줄로 접히던 것은 <b>버튼 자리 탓이 아니라 이름 길이 탓</b>
        이었다(2026-08-31). 자리를 옮기지 않고 이름을 줄였고, 줄이 모자랄 때는 접지 않고 옆으로
        밀도록 했다(`index.css` 의 `.modal-header-actions`).
      */
      actions={
        <>
          {/*
            실험실 스위치는 <b>제목 줄</b>에 있다. 본문에 두면 이 모달의 주인공(위젯 두 목록) 위를
            가로로 가로막아 먼저 읽힌다 — 곁다리 설정에 줄 자리가 아니다. 여기서는 저장 버튼들과
            같은 무게로, 세로 선 하나로만 갈라 둔다.
          */}
          <LabToggle
            icon="message"
            label="AI 인사말"
            /*
              <b>끄면 오브도 함께 조용해진다</b>(사용자 지시 2026-09-18). 그 사실을 여기 적는
              이유는, 스위치 이름이 「AI 인사말」인데 꺼지는 것이 둘이기 때문이다 — 적지 않으면
              말풍선이 왜 멎었는지 알 자리가 어디에도 없다.
            */
            hint={
              '켜면 날씨·일정·할일·연차를 보고 홈 제목에 한 마디를 덧붙이고, ' +
              '떠 있는 AI 오브도 가끔 말을 겁니다. ' +
              '꺼져 있으면 「○○ 님, 안녕하세요」 만 표시되고 오브는 말하지 않습니다.'
            }
            enabled={greeting.enabled}
            saving={greeting.save.isPending}
            onChange={(next) => greeting.save.mutate(next)}
          />
          <span aria-hidden style={{ width: '1px', alignSelf: 'stretch', margin: '0 2px', background: 'var(--border)' }} />

          {/*
            「기본값으로 되돌리기」 였다. 이름은 <b>동사 하나</b>로 줄이고 무엇이 되돌아가는지는
            툴팁에 남긴다 — 버튼 이름이 설명을 겸하면 화면이 좁아질수록 그 설명이 먼저 깨진다.
          */}
          <button
            className="btn-ghost btn-sm"
            title="위젯 구성을 처음 상태로 되돌립니다"
            onClick={() => {
              onReset();
              onClose();
            }}
          >
            되돌리기
          </button>

          {/*
            <b>배치 커스텀</b> — 여기서 끝내지 않고 홈으로 넘긴다. 배치는 보면서 정하는 일이라
            모달 안의 축소판으로는 고를 수 없다({@link WidgetLayoutEditor}).

            <p>넘기기 전에 <b>편집 중인 목록을 들고 간다</b>. 이 모달에서 방금 켠 위젯이 있는데
            저장하지 않고 넘어가면, 배치 화면에는 그 위젯이 없고 저장하는 순간 사라진다.
          */}
          <button
            className="btn-ghost btn-sm"
            title="홈에서 위젯의 자리와 크기를 직접 맞춥니다"
            onClick={() => {
              onCustomize(draft);
              onClose();
            }}
          >
            <MenuIcon name="grid" size={13} />
            배치 커스텀
          </button>
          <button className="btn-secondary btn-sm" onClick={onClose}>
            취소
          </button>
          <button
            className="btn-primary btn-sm"
            onClick={() => {
              onSave(draft);
              onClose();
            }}
          >
            저장
          </button>
        </>
      }
    >
      <p className="text-ui-sm" style={{ color: 'var(--text-muted)', wordBreak: 'keep-all' }}>
        홈 화면에 표시할 위젯을 고르고 순서를 바꿉니다. 보이는 위젯은 소속과 권한에 따라 다릅니다.
      </p>

      <FontScalePicker />

      <div className="grid gap-4 md:grid-cols-2">
        <section>
          <h3 className="mb-2 text-ui-sm" style={{ fontWeight: 700, color: 'var(--text-secondary)' }}>
            표시 중 ({visible.length})
          </h3>

          <div className="space-y-1">
            {visible.map((slot) => {
              const definition = widgetByKey(slot.widgetKey);
              if (!definition) return null;

              return (
                <div
                  key={slot.widgetKey}
                  className="flex items-center gap-2 px-2 py-2 rounded-lg"
                  style={{ border: '1px solid var(--border)', ...dragStyle(slot.widgetKey) }}
                  {...dragHandlers(slot.widgetKey)}
                >
                  <span className="flex-shrink-0" style={{ color: 'var(--text-muted)', cursor: 'grab' }} aria-hidden>
                    <MenuIcon name="drag" size={13} />
                  </span>
                  <span className="flex-shrink-0" style={{ color: 'var(--accent)' }}>
                    <MenuIcon name={definition.icon} size={14} />
                  </span>
                  <span className="min-w-0 flex-1 truncate text-ui-md" style={{ color: 'var(--text-primary)' }}>
                    {definition.title}
                  </span>

                  <SizeToggle slot={slot} onChange={(size) => setSize(slot.widgetKey, size)} />

                  {/*
                    조건이 켠 위젯에는 <b>「자동」</b> 을 달되 닫는 버튼은 남긴다 — 왜 갑자기 생겼는지
                    말해 주지 않으면 사람은 자기가 켠 줄 알고, 닫지 못하면 다음부터 그 자리를 안 본다.
                  */}
                  {forced.has(slot.widgetKey) && !definition.fixed && (
                    <span
                      className="badge badge-blue badge-sm flex-shrink-0"
                      title="진행 중인 일이 있어 저절로 켜졌습니다. 닫으면 그 일이 끝날 때까지 다시 뜨지 않습니다."
                    >
                      자동
                    </span>
                  )}

                  {pinned(slot.widgetKey) ? (
                    <span className="badge badge-gray badge-sm flex-shrink-0">
                      고정
                    </span>
                  ) : (
                    <button
                      className="btn-ghost p-1 flex-shrink-0"
                      onClick={() => setVisibility(slot.widgetKey, false)}
                      aria-label={`${definition.title} 숨기기`}
                    >
                      <MenuIcon name="x" size={13} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          <p className="mt-2 text-ui-xs" style={{ color: 'var(--text-muted)' }}>
            끌어서 순서를 바꿉니다.
          </p>
        </section>

        <section>
          <h3 className="mb-2 text-ui-sm" style={{ fontWeight: 700, color: 'var(--text-secondary)' }}>
            추가할 수 있는 위젯
          </h3>

          <input
            className="input-field input-sm mb-2"
            placeholder="위젯 검색"
            value={keyword}
            onChange={(event) => setKeyword(event.target.value)}
          />

          <div className="space-y-1">
            {hidden.filter(matches).map((slot) => {
              const definition = widgetByKey(slot.widgetKey);
              if (!definition) return null;

              return (
                <button
                  key={slot.widgetKey}
                  onClick={() => setVisibility(slot.widgetKey, true)}
                  className="w-full flex items-start gap-2 px-2 py-2 rounded-lg text-left hover:bg-slate-50"
                  style={{ border: '1px solid var(--border)' }}
                >
                  <span className="flex-shrink-0 mt-0.5" style={{ color: 'var(--accent)' }}>
                    <MenuIcon name="plus" size={13} />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-ui-md" style={{ color: 'var(--text-primary)' }}>
                      {definition.title}
                    </span>
                    <span className="block text-ui-xs" style={{ color: 'var(--text-muted)', wordBreak: 'keep-all' }}>
                      {definition.description}
                    </span>
                  </span>
                </button>
              );
            })}

            {hidden.filter(matches).length === 0 && (
              <p className="py-6 text-center text-ui-sm" style={{ color: 'var(--text-muted)' }}>
                {hidden.length === 0 ? '모든 위젯을 표시 중입니다.' : '검색 결과가 없습니다.'}
              </p>
            )}
          </div>

          <p className="mt-2 text-ui-xs" style={{ color: 'var(--text-muted)', wordBreak: 'keep-all' }}>
            권한이 없는 메뉴의 위젯은 목록에 나오지 않습니다.
          </p>
        </section>
      </div>
    </Modal>
  );
}

/**
 * 실험실 스위치 하나. 모달 제목 줄에 얹는 <b>한 줄짜리</b>다 — 지금은 하나만 서 있다.
 *
 *  - <b>AI 인사말</b>(DOC-66) — 홈 제목 줄에 한 마디를 덧붙인다. 계정에 남는다
 *
 * ## 설명이 왜 툴팁으로 갔는가
 *
 * 껐을 때 무엇이 남는지는 반드시 알려야 한다 — 「끕니다」 만 있으면 사용자는 인사말 자체가
 * 사라지는 줄 알고 누르지 못한다(실제로는 예전 그대로의 「○○ 님, 안녕하세요」 가 남는다).
 * 다만 제목 줄에는 두 줄을 놓을 자리가 없고, 이 설명은 <b>누르기 직전에 한 번</b> 필요한
 * 것이지 늘 펼쳐 둘 것이 아니다. 그래서 `title` 로 옮겼다.
 *
 * 「실험실」은 작게 붙인다. 아직 시험 중이라는 표시는 필요하지만, 그것이 기능 이름만큼
 * 커지면 무엇을 켜는 스위치인지부터 헷갈린다. 배지는 <b>스위치마다 각각</b> 단다 — 여럿이
 * 설 때 하나로 묶으면 그중 무엇이 시험 중인지가 사라진다.
 *
 * 좁은 화면에서는 <b>이름을 접고 아이콘만</b> 남긴다(제목 줄에 버튼이 셋 더 있다). 아이콘이
 * 서로 달라야 하는 이유가 여기 있다 — 접힌 뒤에는 그것이 유일한 구분이다.
 */
function LabToggle({
  icon,
  label,
  hint,
  enabled,
  saving = false,
  onChange,
}: {
  icon: string;
  label: string;
  hint: string;
  enabled: boolean;
  /** 서버에 저장 중인가. 기기에만 남는 스위치는 줄 일이 없다. */
  saving?: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <span className="flex items-center gap-1.5" title={hint}>
      <span className="flex-shrink-0" style={{ color: 'var(--accent)' }}>
        <MenuIcon name={icon} size={13} />
      </span>

      <span className="hidden lg:inline text-ui-sm whitespace-nowrap" style={{ color: 'var(--text-secondary)' }}>
        {label}
      </span>
      <span
        className="flex-shrink-0 rounded text-ui-2xs"
        style={{
          fontWeight: 700,
          lineHeight: '14px',
          padding: '0 4px',
          color: 'var(--accent)',
          background: 'var(--accent-bg)',
        }}
      >
        실험실
      </span>

      <Switch checked={enabled} onChange={onChange} label={label} disabled={saving} />
    </span>
  );
}

/**
 * 글씨 크기 — 권장을 가운데 두고 양쪽으로 두 단씩.
 *
 * ## 왜 여기 있나
 *
 * 홈을 자기 화면으로 맞추는 일이 전부 이 판에 있다(무엇을 볼지·어떤 순서로·어느 폭으로).
 * 크기만 환경설정으로 보내면, 위젯이 빽빽하다고 느낀 사람이 그 판을 열어 놓고도 답을 못 찾는다.
 *
 * ## 왜 [저장] 을 기다리지 않나
 *
 * 위젯 배치는 [저장] 을 눌러야 반영되지만 이것은 <b>끄는 즉시</b> 화면 전체가 바뀐다 —
 * 글씨 크기는 «설명을 읽고 고르는» 것이 아니라 «바꿔 보고 눈으로 고르는» 값이다. 모달을 닫고
 * 확인한 뒤 다시 열어야 한다면 아무도 두 단 이상 시도하지 않는다. 되돌릴 것도 손잡이를 되돌리면
 * 그만이라 취소가 필요 없다(AI 인사말 스위치와 같은 이유).
 *
 * ## 왜 단추 다섯이 아니라 손잡이인가
 *
 * 처음에는 단추 다섯이었는데, 그러면 <b>고르는 일이 다섯 번의 결정</b>이 된다 — 이름을 읽고,
 * 어느 것이 지금인지 찾고, 한 칸 옆을 눌러 보고, 다시 찾는다. 손잡이는 한 번 잡고 끄는 동안
 * 화면이 따라 변해서, <b>이름을 안 읽고도</b> 멈출 자리를 눈으로 찾을 수 있다. 다섯 단으로
 * 끊어 둔 것(`step=1`)은 그대로다 — 무단계로 두면 되돌아올 「권장」 이 사라진다.
 *
 * ## 무엇이 커지지 않는가
 *
 * <b>글씨만 바뀐다</b>(사용자 지시). 배율은 `--fs-*` 눈금에만 곱해지고 여백·아이콘·카드 폭은
 * px 그대로라, 크게 골라도 위젯이 옆으로 부풀지 않는다. 줄이 늘어 카드가 세로로 길어지는 것은
 * 메이슨리가 다시 재어 자리를 잡는다.
 */
function FontScalePicker() {
  const [scale, setScale] = useState<FontScale>(readFontScale);

  const choose = (next: FontScale) => {
    setScale(next);
    saveFontScale(next);
  };

  const index = FONT_SCALES.indexOf(scale);

  return (
    <div
      className="mt-3 px-3 py-2.5 rounded-lg"
      style={{ border: '1px solid var(--border)', background: 'var(--bg-hover)' }}
    >
      <div className="flex items-center gap-2">
        <span className="flex-shrink-0" style={{ color: 'var(--accent)' }}>
          <MenuIcon name="type" size={13} />
        </span>
        <span className="flex-shrink-0 text-ui-sm" style={{ fontWeight: 600, color: 'var(--text-secondary)' }}>
          글씨 크기
        </span>
        {/* 지금 어느 단인지 <b>말로</b> 적는다 — 손잡이 위치만으로는 「권장」 이 어디인지 모른다. */}
        <span className="badge badge-blue badge-sm flex-shrink-0">{FONT_SCALE_LABELS[scale]}</span>

        <input
          type="range"
          className="fs-slider min-w-0 flex-1"
          min={0}
          max={FONT_SCALES.length - 1}
          step={1}
          value={index}
          onChange={(event) => choose(FONT_SCALES[Number(event.target.value)] as FontScale)}
          aria-label="글씨 크기"
          aria-valuetext={FONT_SCALE_LABELS[scale]}
          list="fs-scale-ticks"
        />
        {/* 눈금을 그려 다섯 단이라는 것을 손잡이를 잡기 전에 알린다. */}
        <datalist id="fs-scale-ticks">
          {FONT_SCALES.map((value) => (
            <option key={value} value={FONT_SCALES.indexOf(value)} />
          ))}
        </datalist>
      </div>

      {/*
        양 끝에만 이름을 적는다. 다섯 단 이름을 모두 깔면 좁은 화면에서 줄이 접히고, 가운데
        「권장」 은 위의 배지가 이미 말하고 있다.
      */}
      <div className="mt-1 flex items-center justify-between text-ui-xs" style={{ color: 'var(--text-muted)' }}>
        <span>작게</span>
        <span>크게</span>
      </div>
    </div>
  );
}

function SizeToggle({ slot, onChange }: { slot: WidgetSlot; onChange: (size: WidgetSize) => void }) {
  const definition = widgetByKey(slot.widgetKey);
  if (!definition || definition.sizes.length < 2) return null;

  const labels: Record<WidgetSize, string> = { sm: '보통', wide: '넓게' };

  return (
    <span className="flex gap-0.5 flex-shrink-0">
      {definition.sizes.map((size) => (
        <button
          key={size}
          onClick={() => onChange(size)}
          className="rounded-md text-ui-xs"
          style={{
            padding: '2px 7px',
            border: '1px solid var(--border-strong)',
            background: slot.size === size ? 'var(--accent-bg)' : 'white',
            color: slot.size === size ? 'var(--accent)' : 'var(--text-muted)',
            fontWeight: slot.size === size ? 600 : 400,
          }}
          aria-pressed={slot.size === size}
        >
          {labels[size]}
        </button>
      ))}
    </span>
  );
}

/**
 * 끌 수 없는가 — <b>코드가 정한 고정만</b>이다(할일·공지).
 *
 * <p>조건이 만든 강제(착공 준비)는 여기 들지 않는다. 저절로 켜지고 앞자리에 서지만
 * <b>닫을 수는 있다</b>(사용자 지시 2026-08-26) — 이미 알고 처리 중인 사람에게 끌 수 없는 알림을
 * 몇 주씩 띄우면, 그 사람은 그 자리를 보지 않는 법을 배운다. 닫은 사실은 조건이 끝날 때까지만
 * 기억한다(`useHomeWidgets` §강제는…).
 */
function pinned(widgetKey: string): boolean {
  return widgetByKey(widgetKey)?.fixed ?? false;
}
